import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { TQuoteDetails, TQuoteLineItem } from "../common/types/quote";
import { TLineItemCategory } from "../common/types/line-item-category";
import { getLineItemsTotalPrice } from "../helpers/quote-helper";
import { getFormattedDate } from "./date-time-helper";
import { VEGETATION_DENSITY_MAP, VEGETATION_DENSITY_TO_INTERNAL } from "../constants/quote";
import { TUser } from "../common/types/user";
import { TNullable } from "../common/types/common";
import { getFormattedCurrency } from "./currency-helper";
import { drawTextCenter, normalizeForPdf, scaleToFit } from "./pdf-helper";

interface Props {
    lineItemsCategoryList: TLineItemCategory[],
    lineItems: TQuoteLineItem[];
    quote: TQuoteDetails;
    totalArea: number;
    user: TUser;
    companyLogo: TNullable<Blob>,
}

const generateAndDownload = async ({
    lineItemsCategoryList,
    lineItems,
    quote,
    totalArea,
    user,
    companyLogo,
}: Props) => {
    const pdfDoc = await PDFDocument.create();
    const page = pdfDoc.addPage([600, 750]);

    const drawText = (text, params) => {
        const normalizedText = normalizeForPdf(text);
        return page.drawText(normalizedText, params);
    };

    const font = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
    const normalFont = await pdfDoc.embedFont(StandardFonts.Helvetica);

    const { width, height } = page.getSize();

    const captionSize = 12;
    const captionSizeSmaller = captionSize - 2;
    const textHeight = font.heightAtSize(captionSize);
    const normalTextHeight = normalFont.heightAtSize(captionSize);
    const padding = 20;
    const tableHeaderHeight = 25;
    const infoHeaderHeight = 18;
    const captionRowColor = rgb(1, 0.92, 0.92);
    const captionColor = rgb(0, 0, 0);
    const borderColor = rgb(0.96, 0.96, 0.96);
    const totalPriceSize = 14;
    const colsXOffset = [50, 280, 480];
    const dateFormat = "MMMM DD, YYYY";

    let yOffset = height - padding;

    // Company img
    if (companyLogo) {
        const mimeType = companyLogo?.type;
        const imageBytes = await companyLogo?.arrayBuffer();
        let image;

        if (mimeType === 'image/png') {

            image = await pdfDoc.embedPng(imageBytes);
        } else if (mimeType === 'image/jpeg') {
            image = await pdfDoc.embedJpg(imageBytes);
        }

        if (image) {
            const { width: origWidth, height: origHeight } = image;
            const { width, height } = scaleToFit(origWidth, origHeight, 80, 80);

            page.drawImage(image, {
                x: padding,
                y: yOffset - Math.ceil(height / 2) - padding,
                width,
                height,
            });
        }
    }

    // Right top campaign data
    const fullName = [user.firstName, user.lastName].join(' ');
    const fullNameWidth = font.widthOfTextAtSize(fullName, captionSize);
    drawText(fullName, { x: width - padding - fullNameWidth, y: yOffset, size: captionSize, font, color: captionColor });

    const phone = user.phone;
    if (phone) {
        yOffset -= infoHeaderHeight;
        const phoneWidth = normalFont.widthOfTextAtSize(phone, captionSizeSmaller);
        drawText(phone, { x: width - padding - phoneWidth, y: yOffset, size: captionSizeSmaller, font: normalFont, color: captionColor });
    }

    const email = user.email;
    if (email) {
        yOffset -= infoHeaderHeight;
        const emailWidth = normalFont.widthOfTextAtSize(email, captionSizeSmaller);
        drawText(email, { x: width - padding - emailWidth, y: yOffset, size: captionSizeSmaller, font: normalFont, color: captionColor });
    }

    yOffset -= 80;

    // Quote title
    drawTextCenter({
        page,
        text: `${quote.company} Quote`,
        font,
        size: 18,
        y: yOffset,
        color: captionColor,
    });

    // Quote Details
    yOffset -= infoHeaderHeight * 2;
    const xInfoRightOffset = width - 200;

    drawText("Job Location", { x: padding, y: yOffset, size: captionSizeSmaller, font, color: captionColor });
    drawText("Estimated Start Date", { x: Math.ceil(width / 2 - 100), y: yOffset, size: captionSizeSmaller, font, color: captionColor });
    drawText([quote.contactFirstName, quote.contactLastName].join(' '), { x: xInfoRightOffset, y: yOffset, size: captionSizeSmaller, font: normalFont, color: captionColor });

    const startDate = quote.startDate ? getFormattedDate(quote.startDate, dateFormat, '-') : '-';
    const location = quote.location || '-';

    yOffset -= infoHeaderHeight;
    drawText(startDate, { x: Math.ceil(width / 2 - 100), y: yOffset, size: captionSizeSmaller, font: normalFont, color: captionColor });
    drawText(quote.contactPhone, { x: xInfoRightOffset, y: yOffset, size: captionSizeSmaller, font: normalFont, color: captionColor });

    let locationLines: string[] = [location];
    if (quote.location) {
        const [locationLine1, ...locationLine2] = location.split(',');
        locationLines = [locationLine1, locationLine2.join(',').trim()];
    }

    locationLines.forEach((line, i) => {
        drawText(line, { x: padding, y: yOffset - i * infoHeaderHeight, size: captionSizeSmaller, font: normalFont, color: captionColor });
    });

    yOffset -= infoHeaderHeight;
    drawText(quote.contactEmail, { x: xInfoRightOffset, y: yOffset, size: captionSizeSmaller, font: normalFont, color: captionColor });

    // End date
    yOffset -= infoHeaderHeight * 2;

    const completionDate = quote.completionDate ? getFormattedDate(quote.completionDate, dateFormat, '-') : '-';
    drawText("Job Area Size", { x: padding, y: yOffset, size: captionSizeSmaller, font, color: captionColor });
    drawText("Estimated Completion Date", { x: Math.ceil(width / 2 - 100), y: yOffset, size: captionSizeSmaller, font, color: captionColor });
    drawText(quote.company, { x: xInfoRightOffset, y: yOffset, size: captionSizeSmaller, font: normalFont, color: captionColor });

    yOffset -= infoHeaderHeight;
    drawText(completionDate, { x: Math.ceil(width / 2 - 100), y: yOffset, size: captionSizeSmaller, font: normalFont, color: captionColor });
    drawText(`${totalArea} Acres`, { x: padding, y: yOffset, size: captionSizeSmaller, font: normalFont, color: captionColor });
    drawText(quote.contactAddress1, { x: xInfoRightOffset, y: yOffset, size: captionSizeSmaller, font: normalFont, color: captionColor });

    if (quote.contactAddress2) {
        yOffset -= infoHeaderHeight;
        drawText(quote.contactAddress2, { x: xInfoRightOffset, y: yOffset, size: captionSizeSmaller, font: normalFont, color: captionColor });
    }

    yOffset -= infoHeaderHeight;
    const addrDetails = `${quote.contactCity}, ${quote.contactState} ${quote.contactZip}`;

    drawText(addrDetails, { x: xInfoRightOffset, y: yOffset, size: captionSizeSmaller, font: normalFont, color: captionColor });

    yOffset -= 70;

    const tableStartY = yOffset;

    page.drawRectangle({
        x: padding,
        y: yOffset,
        width: width - padding * 2,
        height: tableHeaderHeight,
        color: rgb(0.96, 0.96, 0.96),
    });

    const y1 = yOffset + Math.ceil((tableHeaderHeight - textHeight) / 2);

    const captions = ["Item", "Cost Calculation", "Total Cost ($)"];

    captions.forEach((title, index) => {
        drawText(title, { x: colsXOffset[index], y: y1, size: captionSize, font, color: captionColor });
    });

    let y2 = yOffset;

    lineItemsCategoryList.forEach(({ id, name }) => {
        y2 -= tableHeaderHeight;
        const categoryLineItems = lineItems.filter(({ lineItemCategoryId }) => lineItemCategoryId === id);
        const total = getLineItemsTotalPrice(categoryLineItems);

        page.drawRectangle({
            x: padding,
            y: y2,
            width: width - padding * 2,
            height: tableHeaderHeight,
            color: captionRowColor,
            borderColor,
            borderWidth: 1,
        });

        const yCategory = y2 + Math.ceil((tableHeaderHeight - textHeight) / 2);

        drawText(name, { x: colsXOffset[0], y: yCategory, size: captionSize, font, color: captionColor });
        drawText(`${getFormattedCurrency(total)}`, { x: colsXOffset[2], y: yCategory, size: captionSize, font, color: captionColor });


        const tablePadding = padding * 0.8;
        categoryLineItems.forEach(({ lineItemCategoryId, description, price }) => {
            y2 -= tableHeaderHeight;
            let yLineItem = y2 + Math.ceil((tableHeaderHeight - normalTextHeight) / 2);

            drawText(`${getFormattedCurrency(price)}`, { x: colsXOffset[1] + tablePadding, y: yLineItem, size: captionSize, font: normalFont, color: captionColor });

            if (lineItemCategoryId === 1) {
                drawText(`Total Acreage (${totalArea} Acres)`, { x: colsXOffset[0] + tablePadding, y: yLineItem, size: captionSize, font: normalFont, color: captionColor });
                const vegetationDensity = quote?.vegetationDensity ? VEGETATION_DENSITY_MAP[VEGETATION_DENSITY_TO_INTERNAL[quote.vegetationDensity]].title : "";

                if (vegetationDensity) {
                    y2 -= tableHeaderHeight;
                    yLineItem -= tableHeaderHeight;
                    drawText(vegetationDensity, { x: colsXOffset[0] + tablePadding * 2, y: yLineItem, size: captionSize, font: normalFont, color: captionColor });
                }
            } else {
                if (description.includes("per hour")) {
                    description = description.split(" - $")[0];
                }
                drawText(description, { x: colsXOffset[0] + tablePadding, y: yLineItem, size: captionSize, font: normalFont, color: captionColor });
            }
        });

    });

    page.drawLine({ start: { x: width - padding, y: y2 }, end: { x: padding, y: y2 }, thickness: 1, color: borderColor });
    page.drawLine({ start: { x: padding, y: tableStartY }, end: { x: padding, y: y2 }, thickness: 1, color: borderColor });
    page.drawLine({ start: { x: width - padding, y: tableStartY }, end: { x: width - padding, y: y2 }, thickness: 1, color: borderColor });

    const totalPriceHeight = font.heightAtSize(totalPriceSize);
    const total = getLineItemsTotalPrice(lineItems);
    y2 -= tableHeaderHeight;
    const yCategory = y2 + Math.ceil((tableHeaderHeight - totalPriceHeight) / 2);

    drawText("TOTAL COST", { x: colsXOffset[1] + 100, y: yCategory, size: totalPriceSize, font, color: captionColor });
    drawText(`${getFormattedCurrency(total)}`, { x: colsXOffset[2], y: yCategory, size: totalPriceSize, font, color: captionColor });

    const pdfBytes = await pdfDoc.save();

    // Create a Blob from the PDF
    const blob = new Blob([pdfBytes], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);

    // Create and trigger a download link
    const link = document.createElement("a");
    link.href = url;
    link.download = "generated.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the URL object
    URL.revokeObjectURL(url);
};

export default generateAndDownload;
